import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Stack, Typography } from '@rossum/ui/material';
import { GridColDef } from '@rossum/ui/x-data-grid-pro';
import { RenderControlledTextInput } from '../../core/components/RenderControlledTextInput';
import { RenderDatapointLabel } from '../../core/components/RenderDatapointLabel';
import {
  LegacyAutomationSettingsFormInput,
  LegacyThresholdsTableRowModel,
} from '../types';

export const NEW_THRESHOLD_INPUT_CLASS_NAME = 'new-threshold-input';

const commonColumnProps = {
  filterable: false,
};

export const useLegacyThresholdsTableColumns = () => {
  const intl = useIntl();

  const { control } = useFormContext<LegacyAutomationSettingsFormInput>();

  return useMemo<GridColDef<LegacyThresholdsTableRowModel>[]>(() => {
    return [
      {
        ...commonColumnProps,
        flex: 1,
        field: 'datapointLabel',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.legacySettings.table.columns.label',
        }),
        valueGetter: (_, row) => row.schemaId,
        renderCell: params => {
          return (
            <RenderDatapointLabel
              datapointLabel={params.row.datapointLabel}
              schemaId={params.row.schemaId}
            />
          );
        },
      },
      {
        ...commonColumnProps,
        field: 'currentThreshold',
        width: 150,
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.legacySettings.table.columns.currentThreshold',
        }),
        valueGetter: (_, row) => row.currentThreshold,
        renderCell: params => {
          return (
            <Stack
              sx={{ justifyContent: 'center', alignItems: 'center', height: 1 }}
            >
              <Typography variant="body2" color="textSecondary">
                {params.row.currentThreshold}
              </Typography>
            </Stack>
          );
        },
      },
      {
        ...commonColumnProps,
        sortable: false,
        field: 'newThreshold',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.legacySettings.table.columns.newThreshold',
        }),
        width: 150,
        renderCell: ({ row }) => {
          return (
            <RenderControlledTextInput
              control={control}
              schemaId={`thresholdValues.${row.schemaId}`}
              errorMessage={row.errorMessage}
              className={NEW_THRESHOLD_INPUT_CLASS_NAME}
            />
          );
        },
      },
    ];
  }, [control, intl]);
};
